import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PageMasthead from '../../components/PageMasthead'
import Button from '../../components/Button'
import InfoCallout from '../../components/callout/InfoCallout'
import { cookieStatusBadge } from '../../components/cookies/statusBadge'
import {
    CookiePlatform,
    useCreateCookieMutation,
    useGetCookieQuery,
    useUpdateCookieMutation
} from '../../api/client'
import { toastError, toastSuccess } from '../../components/Notification'

type FormMode = 'new' | 'edit'

type CookieForm = {
    platform: CookiePlatform
    serviceUsername: string
    cookieJson: string
    notes?: string
}

const CookieFormPage: React.FC = () => {
    const navigate = useNavigate()
    const { cookieId } = useParams<{ cookieId: string }>()
    const mode: FormMode = cookieId ? 'edit' : 'new'

    const [form, setForm] = useState<CookieForm>({
        platform: CookiePlatform.Instagram,
        serviceUsername: '',
        cookieJson: '',
        notes: ''
    })

    const { data: cookieData, loading } = useGetCookieQuery({
        variables: { cookieId: Number(cookieId) },
        skip: !cookieId
    })
    const [createCookie, { loading: creating }] = useCreateCookieMutation({
        refetchQueries: ['GetCookies']
    })
    const [updateCookie, { loading: updating }] = useUpdateCookieMutation({
        refetchQueries: ['GetCookies']
    })

    useEffect(() => {
        if (mode === 'edit' && cookieData?.getCookie) {
            const existing = cookieData.getCookie
            setForm({
                platform: existing.platform,
                serviceUsername: existing.serviceUsername,
                cookieJson: '',
                notes: existing.notes || ''
            })
        }
    }, [cookieData, mode])

    const save = async () => {
        try {
            if (mode === 'edit' && cookieId) {
                // Edit mode: only update metadata (cookie JSON stays secret)
                await updateCookie({
                    variables: {
                        input: {
                            cookieId: Number(cookieId),
                            notes: form.notes ? form.notes : null
                        }
                    }
                })
            } else {
                // Create mode: validate and require cookie JSON
                if (!form.serviceUsername || !form.serviceUsername.trim()) {
                    toastError('Account username is required.')
                    return
                }

                if (!form.cookieJson || !form.cookieJson.trim()) {
                    toastError('Cookie JSON is required.')
                    return
                }

                try {
                    const parsed = JSON.parse(form.cookieJson)
                    if (!Array.isArray(parsed)) {
                        toastError('Cookie JSON should be an array (e.g., cookies.txt export).')
                        return
                    }
                } catch {
                    toastError('Invalid JSON for cookie.')
                    return
                }

                await createCookie({
                    variables: {
                        input: {
                            platform: form.platform,
                            serviceUsername: form.serviceUsername,
                            cookieJson: form.cookieJson,
                            notes: form.notes || null
                        }
                    }
                })
            }
            toastSuccess('Cookie saved.')
            navigate('/admin/cookie')
        } catch (err) {
            console.error('Failed to save cookie:', err)
            toastError('Failed to save cookie. Please try again.')
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-ink-50 flex items-center justify-center">
                <div className="text-ink-600">Loading...</div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead
                title={mode === 'new' ? 'New cookie' : 'Edit cookie'}
                tabTitle={mode === 'new' ? 'New cookie' : 'Edit cookie'}
                subtitle={mode === 'new' ? 'Add a new cookie' : 'Update cookie metadata (cookie JSON cannot be changed)'}
            />
            {/* max-w rather than w-2/3: a percentage width stretches the fields past a
                readable measure on a wide screen. px-6 py-6 lines up with CookieListPage. */}
            <div className="max-w-3xl space-y-6 px-6 py-6">
                <p className="font-sans text-sm text-ink-500">
                    Cookies are encrypted at rest. Supports Twitter/X, Facebook, and Instagram.
                </p>
                <InfoCallout>
                    <p className="font-medium text-ink-900">How to capture a cookie</p>
                    <p className="mt-1">
                        Log in with a test profile for the selected platform in Chrome, then export cookies with the{' '}
                        <a
                            className="underline"
                            href="https://chromewebstore.google.com/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Get cookies.txt locally
                        </a>{' '}
                        extension. Paste the JSON array here. The cookie will be encrypted and
                        stored.
                    </p>
                </InfoCallout>

                {/* Card treatment matches components/card/Card.tsx so this form reads
                    as the same surface as the rest of the app against bg-ink-50. */}
                <div className="space-y-4 rounded-2xl border border-ink-200 bg-white p-6 shadow-sm">
                    <div>
                        <label className="block text-sm font-medium text-ink-700">Platform</label>
                        <select
                            className="mt-1 block w-full border border-ink-200 rounded-md shadow-sm focus:ring-arwen-500 focus:border-arwen-500 sm:text-sm"
                            value={form.platform}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    platform: e.target.value as CookiePlatform
                                }))
                            }
                            disabled={mode === 'edit'}
                        >
                            <option value={CookiePlatform.Twitter}>Twitter/X</option>
                            <option value={CookiePlatform.Facebook}>Facebook</option>
                            <option value={CookiePlatform.Instagram}>Instagram</option>
                        </select>
                        <p className="text-xs text-ink-500 mt-1">
                            {mode === 'edit' ? 'Platform cannot be changed after creation.' : 'Select the platform for this cookie.'}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Account username
                        </label>
                        <input
                            type="text"
                            className="mt-1 block w-full border border-ink-200 rounded-md shadow-sm focus:ring-arwen-500 focus:border-arwen-500 sm:text-sm"
                            value={form.serviceUsername}
                            onChange={(e) => setForm((f) => ({ ...f, serviceUsername: e.target.value }))}
                            disabled={mode === 'edit'}
                            placeholder="e.g., @username"
                            maxLength={50}
                        />
                        <p className="text-xs text-ink-500 mt-1">
                            {mode === 'edit' ? 'Username cannot be changed after creation.' : 'The username of the account used to create this cookie.'}
                        </p>
                    </div>

                    {mode === 'new' && (
                        <div>
                            <label className="block text-sm font-medium text-ink-700">
                                Cookie JSON (array)
                            </label>
                            <textarea
                                className="mt-1 block w-full border border-ink-200 rounded-md shadow-sm focus:ring-arwen-500 focus:border-arwen-500 sm:text-sm"
                                rows={8}
                                placeholder='[{"domain":".instagram.com","name":"sessionid","value":"..."}]'
                                value={form.cookieJson}
                                onChange={(e) => setForm((f) => ({ ...f, cookieJson: e.target.value }))}
                            />
                            <p className="text-xs text-ink-500 mt-1">
                                Required. Cookie JSON will be encrypted and cannot be viewed or changed after creation.
                            </p>
                        </div>
                    )}

                    {mode === 'edit' && cookieData?.getCookie && (
                        <>
                            <div className="bg-ink-100 border border-ink-200 rounded-md p-4">
                                <p className="text-sm text-ink-600">
                                    <strong>Note:</strong> The cookie JSON is encrypted and kept secret. You can only edit the metadata below.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink-700">Status</label>
                                <div className="mt-1">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cookieStatusBadge(
                                            cookieData.getCookie.status
                                        )}`}
                                    >
                                        {cookieData.getCookie.status}
                                    </span>
                                </div>
                                <p className="text-xs text-ink-500 mt-1">
                                    Status can be changed from the cookie list page.
                                </p>
                            </div>
                        </>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Notes (optional)
                        </label>
                        <input
                            type="text"
                            className="mt-1 block w-full border border-ink-200 rounded-md shadow-sm focus:ring-arwen-500 focus:border-arwen-500 sm:text-sm"
                            value={form.notes || ''}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        />
                    </div>

                    <div className="flex items-center justify-between pt-4">
                        <div className="text-xs text-ink-500">
                            Cookies are encrypted with the Cookie KMS key at rest. Only metadata is
                            shown here; cookie payloads are not displayed.
                        </div>
                        <div className="flex space-x-3">
                            <Button
                                text="Cancel"
                                primary={false}
                                type="button"
                                size="medium"
                                onClick={() => navigate('/admin/cookie')}
                            />
                            <Button
                                text="Save"
                                primary={true}
                                type="button"
                                size="medium"
                                onClick={save}
                                loading={creating || updating}
                                disabled={creating || updating}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CookieFormPage
